import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { StatusBadge } from 'components/StatusBadge';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { Download, Send } from 'lucide-react';
import { formatDateTime } from 'lib/format';
import { useLanguage } from 'lib/i18n';
import { CONTACT_EMAIL } from 'lib/contact';

const MAX_ATTACHMENTS = 5;

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

function TicketCard({ ticket, onReplied }) {
  const { t } = useLanguage();
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const closed = ticket.status === 'closed';

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const response = await api.post(`/support/tickets/${ticket.id}/reply`, { message: reply.trim() });
      setReply('');
      onReplied(response.data);
    } catch (error) { toast.error(getApiErrorMessage(error, t('support.replyError'))); }
    finally { setSending(false); }
  };

  return (
    <div className="border-b last:border-0 pb-3 last:pb-0">
      <div className="flex justify-between gap-3"><strong className="text-sm">{ticket.subject}</strong><StatusBadge status={ticket.status} /></div>
      <p className="mt-1 text-sm text-muted-foreground">{ticket.description}</p>
      {(ticket.replies || []).length > 0 && (
        <div className="mt-2 space-y-2 pl-3 border-l-2">
          {ticket.replies.map((r, i) => (
            <div key={i} className="text-sm">
              <span className={`font-medium ${r.is_admin ? 'text-primary' : ''}`}>{r.is_admin ? t('support.team') : t('support.you')}</span>
              <span className="text-xs text-muted-foreground ml-2">{formatDateTime(r.created_at)}</span>
              <p className="text-muted-foreground">{r.message}</p>
            </div>
          ))}
        </div>
      )}
      {!closed && (
        <div className="mt-2 flex gap-2">
          <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t('support.replyPlaceholder')} />
          <Button size="sm" onClick={sendReply} disabled={sending || !reply.trim()}>{t('support.send')}</Button>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const load = () => api.get('/support/tickets').then((response) => setTickets(response.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, MAX_ATTACHMENTS);
    if (files.length === 0) return;
    try {
      const dataUrls = await Promise.all(files.map(readFileAsDataUrl));
      setAttachments(dataUrls);
    } catch { toast.error(t('support.fileReadError')); }
  };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post('/support/tickets', { subject, description, attachments });
      toast.success(t('support.requestSent'));
      setSubject(''); setDescription(''); setAttachments([]); load();
    } catch (error) { toast.error(getApiErrorMessage(error, t('support.sendError'))); }
    finally { setSaving(false); }
  };
  const exportTransactions = async () => {
    setExporting(true);
    try {
      const response = await api.get('/transactions/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'colibuddy-transactions.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { toast.error(getApiErrorMessage(error, t('support.exportError'))); }
    finally { setExporting(false); }
  };
  return (
    <DashboardShell>
      <div className="max-w-3xl space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">{t('support.title')}</h1>
            <p className="text-muted-foreground">{t('support.subtitle')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('support.emailContact')}{' '}
              <a className="font-medium text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </div>
          <Button variant="outline" onClick={exportTransactions} disabled={exporting}>
            <Download size={15} className="mr-1" />{exporting ? t('support.exporting') : t('support.exportTransactions')}
          </Button>
        </div>
        <Card>
          <CardHeader><CardTitle className="text-base">{t('support.newRequest')}</CardTitle></CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={submit}>
              <div><Label>{t('support.subject')}</Label><Input required value={subject} onChange={(event) => setSubject(event.target.value)} /></div>
              <div><Label>{t('support.message')}</Label><Textarea required value={description} onChange={(event) => setDescription(event.target.value)} /></div>
              <div>
                <Label>{t('support.attachmentsLabel').replace('{max}', MAX_ATTACHMENTS)}</Label>
                <Input type="file" multiple accept="image/*,.pdf" onChange={handleFiles} />
                <p className="text-xs text-muted-foreground mt-1">
                  {attachments.length > 0 ? t(attachments.length === 1 ? 'support.filesLoaded.one' : 'support.filesLoaded.many').replace('{count}', attachments.length) : t('support.noFileSelected')}
                </p>
              </div>
              <Button disabled={saving}><Send size={15} className="mr-1" />{saving ? t('support.sending') : t('support.send')}</Button>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t('support.myRequests')}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {tickets.length === 0 ? <p className="text-sm text-muted-foreground">{t('support.noRequests')}</p> : tickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} onReplied={(updated) => setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))} />
            ))}
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}